/**
 * StockPage — `/estoque`: a posição atual das unidades estocáveis.
 *
 * PRO no plano (`inventory`), sem portão de papel: a RLS de `products` é
 * `is_company_member`.
 *
 * ─── O DESENHO OFICIAL NUMA LISTA SEM DINHEIRO ──────────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`. O que precisou ser TRADUZIDO:
 *
 * · O VALOR AO VIVO É A CONTAGEM DO RECORTE, não dinheiro — a mesma tradução de
 *   `/clients` e `/catalog` (#195). Havia um candidato monetário óbvio, o
 *   "Valor investido" (saldo × custo) do relatório, e ele foi descartado de
 *   propósito: `products.cost` é NULLABLE (#61), então quem não preenche custo
 *   veria **R$ 0,00 com a prateleira cheia** — a mesma mentira em `text-3xl` que
 *   o #187 descreve, aqui provocada por ausência de dado em vez de recusa de
 *   leitura. A contagem é honesta em qualquer cadastro, e o número investido
 *   continua no Relatório de Estoque, onde a tela cabe dizer quantas unidades
 *   ficaram fora da conta.
 *
 * · A SUBLINHA CARREGA O ACIONÁVEL ("N precisando de atenção") e é ela que
 *   substitui o antigo cartão de alerta de estoque baixo. O cartão não "sumiu":
 *   ele dizia o mesmo número dois centímetros abaixo do hero, que é a
 *   duplicação que o #200 removeu do Financeiro — e agora o número é
 *   CLICÁVEL, porque virou o botão "Com alerta" da faixa de recorte.
 *
 * · A FAIXA DE SITUAÇÃO usa os baldes EXCLUSIVOS do `alertKindOf` (#52), que é
 *   o que faz a soma dos botões fechar com a sublinha. O selo do card veio da
 *   mesma função, então um saldo NEGATIVO passou a se anunciar na lista — antes
 *   ele só ficava vermelho, e o único selo possível era "Baixo".
 *
 * · NÃO HÁ AÇÃO PRIMÁRIA no hero, e isso não é esquecimento: produto se cadastra
 *   no catálogo (é de lá que sai o "Controlar estoque"), e inventar um "Novo"
 *   aqui criaria um caminho que a tela não sabe executar (#198). O que existe é
 *   a engrenagem de quando dar baixa, que é secundária e vira ícone (#186).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  ArrowUpDown,
  AlertTriangle,
  Boxes,
  Check,
  Crown,
  Lock,
  Package,
  Search,
  Settings,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeature } from '@/hooks/useFeature';
import { useStock } from '@/hooks/useStock';
import { formatStockQuantity } from '@/lib/stock';
import { alertKindOf, STOCK_ALERT_BADGE_LABELS } from '@/lib/stockReport';
import { StockMovementModal } from '@/components/stock/StockMovementModal';
import { StockSettingsModal } from '@/components/stock/StockSettingsModal';
import {
  ALL_CATEGORIES,
  DEFAULT_STOCK_SORT,
  STOCK_SITUATION_FILTERS,
  STOCK_SORT_LABELS,
  STOCK_SORT_ORDER,
  countActiveStockFilters,
  countStockUnitsBySituation,
  matchesStockCategory,
  matchesStockSituation,
  sortStockList,
  stockCategories,
  stockListTotalLabel,
  stockUnitMatchesSearch,
  summarizeStockList,
  type StockSituationFilter,
  type StockSortOption,
} from '@/lib/stockListFilters';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { plural } from '@/lib/plural';
import { cn } from '@/lib/utils';

const CATEGORY_GROUP_KEY = 'category';

export default function StockPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inventory = useFeature('inventory');
  const { trackedProducts, isLoading, productById } = useStock();

  const [search, setSearch] = useState('');
  const [situation, setSituation] = useState<StockSituationFilter>('all');
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const [sortOption, setSortOption] = useState<StockSortOption>(DEFAULT_STOCK_SORT);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /** Rótulo da variação ("P") e o nome do pai, para dar contexto na lista. */
  const parentNameOf = (p: any): string | null => {
    if (!p?.parent_product_id) return null;
    const parent = productById.get(String(p.parent_product_id));
    return parent?.name ? String(parent.name) : null;
  };

  /**
   * O recorte SEM a situação — é ele que alimenta as contagens dos botões
   * (#193). Contar sobre a lista inteira faria cada botão prometer um número
   * que a lista não entrega depois do clique.
   */
  const beforeSituation = useMemo(
    () =>
      (trackedProducts as any[]).filter(
        (p) =>
          stockUnitMatchesSearch(p, parentNameOf(p), search) && matchesStockCategory(p, category),
      ),
    [trackedProducts, search, category, productById],
  );

  const situationCounts = useMemo(
    () => countStockUnitsBySituation(beforeSituation),
    [beforeSituation],
  );

  const filtered = useMemo(
    () =>
      sortStockList(
        beforeSituation.filter((p) => matchesStockSituation(p, situation)),
        sortOption,
        parentNameOf,
      ),
    [beforeSituation, situation, sortOption, productById],
  );

  const summary = summarizeStockList(filtered);
  const categories = useMemo(() => stockCategories(trackedProducts as any[]), [trackedProducts]);
  const activeModalFilters = countActiveStockFilters(category);
  const recorteAtivo =
    search.trim().length > 0 || situation !== 'all' || activeModalFilters > 0;

  const filterGroups: FilterGroup[] = [
    {
      key: CATEGORY_GROUP_KEY,
      label: 'Categoria',
      options: [
        // "Todas" é uma opção EXPLÍCITA, e não a ausência de seleção: sem ela,
        // quem escolheu uma categoria não teria como voltar atrás.
        { value: ALL_CATEGORIES, label: 'Todas' },
        ...categories.map((name) => ({ value: name, label: name })),
      ],
    },
  ];

  const openMovement = (product: any) => {
    setSelected(product);
    setModalOpen(true);
  };

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Boxes className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Estoque</h2>
          <p className="mb-4 text-muted-foreground">Faça login para acessar o estoque</p>
          <Button3D onClick={() => navigate('/auth')}>Entrar</Button3D>
        </div>
      </MainLayout>
    );
  }

  // Feature gate: usuários free veem a tela bloqueada com CTA de upsell.
  // Sem hero aqui, como no portão de papel do `/financeiro`: um cabeçalho que
  // anuncia um número (ou uma faixa de filtros) sobre uma lista que a pessoa não
  // pode ver seria informação sobre dado inacessível (#199).
  if (!inventory.allowed) {
    return (
      <MainLayout>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Controle de estoque</h2>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              Acompanhe a quantidade dos seus produtos, receba alertas de estoque baixo e dê baixa nas
              vendas. Disponível no plano PRO.
            </p>
            <Button3D color="amber" className="mt-5" onClick={() => inventory.require()}>
              <Crown className="mr-2 h-4 w-4" />
              Desbloquear com PRO
            </Button3D>
            {/* O cabeçalho antigo tinha a seta de voltar; sem ela, sair da tela
                bloqueada dependeria do gesto do sistema. */}
            <Button variant="outline" className="mt-3" onClick={() => navigate('/dashboard')}>
              Voltar ao início
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app (o mesmo de /quotes)                */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            {/* `/estoque` não está na navegação inferior: sem este botão, a
                única saída da tela é o gesto do sistema. */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate('/dashboard')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Catálogo</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">Estoque</h1>
            </div>
            {/* Ação SECUNDÁRIA vira ícone (#186), com o nome no
                `aria-label`/`title` — sem eles o botão perde o nome acessível
                junto com o texto (#142). */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => setSettingsOpen(true)}
              aria-label="Quando dar baixa no estoque"
              title="Quando dar baixa no estoque"
            >
              <Settings className="h-5 w-5" />
            </Button>
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">{stockListTotalLabel(recorteAtivo)}</p>
            {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                (#159-b): aqui a contagem nunca chega perto do limite, mas uma
                regra local seria mais uma cópia de uma medida que já divergiu
                entre telas uma vez. */}
            <p
              className={cn(
                'truncate font-bold leading-tight',
                heroTotalSizeClass(String(summary.count)),
              )}
            >
              {summary.count}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {/* Enquanto a lista carrega, o hero NÃO afirma que está vazia: um
                  "nenhum produto" que aparece antes da resposta é uma afirmação
                  falsa em cima de um zero igualmente falso (#199). */}
              {summary.count === 0
                ? isLoading
                  ? 'Carregando…'
                  : recorteAtivo
                    ? 'Nenhum produto neste filtro'
                    : 'Nenhum produto com estoque'
                : summary.alertCount > 0
                  ? `${plural(summary.alertCount, 'produto', 'produtos')} precisando de atenção`
                  : 'Nenhum alerta de estoque'}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* BLOCO DE CONTROLES DA LISTA — busca, situação e a lista.
              Todos respondem "o que eu estou vendo", então ficam num grupo só
              com `space-y-2`; a LISTA fica DENTRO dele, e não como irmã com
              `-mt-N`, porque margem negativa num filho direto perde a briga de
              especificidade contra o `space-y-*` do container (#194). */}
          <div className="space-y-2">
            {/* A busca e a faixa só existem quando há produto rastreado: sem
                nenhum, o que a tela precisa dizer é COMO habilitar o rastreio. */}
            {trackedProducts.length > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <div className="relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="pl-10"
                      placeholder="Buscar produto…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  {/* Ordenação: menu, não modal — é uma escolha única entre
                      rótulos curtos, e um diálogo custaria dois cliques a mais
                      pelo mesmo resultado. */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0 rounded-xl"
                        aria-label={`Ordenar (${STOCK_SORT_LABELS[sortOption]})`}
                        title={`Ordenar: ${STOCK_SORT_LABELS[sortOption]}`}
                      >
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {STOCK_SORT_ORDER.map((option) => (
                        <DropdownMenuItem
                          key={option}
                          onSelect={() => setSortOption(option)}
                          className="gap-2"
                        >
                          {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                              placeholder, o rótulo escolhido fica deslocado. */}
                          <span className="flex h-4 w-4 items-center justify-center">
                            {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                          </span>
                          {STOCK_SORT_LABELS[option]}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* A CATEGORIA vai para o modal, e não para a faixa: a faixa
                      já é da situação, que é o recorte acionável desta tela. */}
                  <Button
                    variant="outline"
                    size="icon"
                    className="relative h-10 w-10 shrink-0 rounded-xl"
                    onClick={() => setFiltersOpen(true)}
                    aria-label={
                      activeModalFilters > 0
                        ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                        : 'Filtros'
                    }
                    title="Filtros"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {activeModalFilters > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                        {activeModalFilters}
                      </span>
                    )}
                  </Button>
                </div>

                {/* SITUAÇÃO — botões planos, não alternador: recorte não pode
                    parecer troca de contexto (#25/#188). A contagem vai no
                    `title`/`aria-label`, onde não custa pixel (#191). */}
                <div className={FILTER_CHIP_ROW}>
                  {STOCK_SITUATION_FILTERS.map((option) => {
                    const active = situation === option.value;
                    const count = situationCounts[option.value] ?? 0;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSituation(option.value)}
                        aria-pressed={active}
                        aria-label={`${option.label} (${count})`}
                        title={`${option.label}: ${plural(count, 'produto', 'produtos')}`}
                        className={cn(
                          FILTER_CHIP_COMPACT,
                          active ? FILTER_CHIP_ON : FILTER_CHIP_OFF,
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>

                {/* A ORDENAÇÃO ativa é a única decisão da tela que não se
                    anuncia sozinha: a situação tem botões e a categoria tem o
                    selo do ícone. É RÓTULO, não botão — o controle é o ícone
                    logo acima, e um segundo gatilho dividiria o alvo do toque. */}
                {filtered.length > 0 && (
                  <div className="flex items-center gap-1.5 px-1 text-sm text-muted-foreground">
                    <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{STOCK_SORT_LABELS[sortOption]}</span>
                  </div>
                )}
              </>
            )}

            {isLoading && trackedProducts.length === 0 ? (
              <p className="py-16 text-center text-sm text-muted-foreground">Carregando…</p>
            ) : trackedProducts.length === 0 ? (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-10 text-center',
                )}
              >
                <Package className="mb-4 h-12 w-12 text-muted-foreground/60" />
                <p className="font-medium">Nenhum produto com estoque</p>
                <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                  Ative o "Controlar estoque" ao cadastrar ou editar um produto no catálogo.
                </p>
                <Button variant="outline" className="mt-5" onClick={() => navigate('/catalog')}>
                  <Package className="mr-2 h-4 w-4" />
                  Ir para o catálogo
                </Button>
              </div>
            ) : filtered.length === 0 ? (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-8 text-center',
                )}
              >
                <Package className="mb-3 h-12 w-12 text-muted-foreground/50" />
                <p className="text-muted-foreground">Nenhum produto encontrado</p>
              </div>
            ) : (
              /* Duas colunas no desktop (mesmo padrão de `/quotes`), com
                 `grid-cols-1` explícito no celular para um nome de produto sem
                 espaços não esticar a coluna e vazar a página (#185). */
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                {filtered.map((p: any) => {
                  const balance = Number(p.stock_quantity ?? 0);
                  const kind = alertKindOf(p);
                  const parent = parentNameOf(p);
                  return (
                    <div
                      key={p.id}
                      className={cn(
                        CONTENT_CARD,
                        'flex cursor-pointer items-center gap-3 p-4 transition-shadow hover:shadow-md',
                      )}
                      onClick={() => openMovement(p)}
                    >
                      {/* Coluna da ESQUERDA: identificação + conteúdo. Ela é
                          quem dita a altura do card — o `items-center` do card
                          faz o bloco saldo+selo centrar contra ELA, e não
                          contra a linha do título (#189). */}
                      <div className="min-w-0 flex-1 space-y-2">
                        {/* LINHA DE IDENTIFICAÇÃO: categoria, mínimo e o selo
                            de pendência offline. Tudo que qualifica a unidade
                            (mas não é o nome dela) mora aqui, o que devolve a
                            largura inteira ao título na linha de baixo. */}
                        {(p.category || p.min_stock != null || p._offline_pending) && (
                          <div className="flex flex-wrap items-center gap-2">
                            {p.category && (
                              <Badge
                                variant="outline"
                                className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                              >
                                {p.category}
                              </Badge>
                            )}
                            {p.min_stock != null && (
                              <Badge
                                variant="outline"
                                className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                              >
                                mín. {formatStockQuantity(Number(p.min_stock))}
                              </Badge>
                            )}
                            {p._offline_pending && (
                              <Badge variant="outline" className="h-5 px-1.5 py-0 text-[10px]">
                                Offline
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Variação: o pai vira contexto acima e o rótulo é o
                            título, para "P" e "M" da mesma camiseta não virarem
                            duas linhas visualmente idênticas (#30d).
                            `min-w-0` + `break-words` e NENHUM `truncate`: é o
                            nome que distingue duas linhas, e cortá-lo come
                            justamente o que informa (#175). */}
                        <div className="min-w-0 space-y-0.5">
                          {p.parent_product_id ? (
                            <>
                              <p className="min-w-0 break-words text-xs text-muted-foreground">
                                {parent ?? p.name}
                              </p>
                              <p className="min-w-0 break-words font-medium">{p.variant_name}</p>
                            </>
                          ) : (
                            <p className="min-w-0 break-words font-medium">{p.name}</p>
                          )}
                        </div>
                      </div>

                      {/* Saldo + situação são UM CONJUNTO, e por isso um bloco
                          só: centrados entre si e centrados na ALTURA DO CARD
                          pelo `items-center` do card (#189). */}
                      <div className="flex shrink-0 flex-col items-center gap-1">
                        <p
                          className={cn(
                            'text-lg font-semibold',
                            kind ? 'text-destructive' : undefined,
                          )}
                        >
                          {formatStockQuantity(balance)}
                          {p.unit ? (
                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                              {p.unit}
                            </span>
                          ) : null}
                        </p>
                        {/* O selo sai do MESMO `alertKindOf` da faixa e da
                            planilha: um saldo negativo passou a se anunciar
                            aqui, e não só a ficar vermelho. Rótulo CURTO
                            (`STOCK_ALERT_BADGE_LABELS`) porque selo largo ao
                            lado de nome variável come o nome (#52). */}
                        {kind && (
                          <Badge variant="destructive" className="gap-1 text-[10px]">
                            <AlertTriangle className="h-3 w-3" />
                            {STOCK_ALERT_BADGE_LABELS[kind]}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <StockSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
      <StockMovementModal open={modalOpen} onOpenChange={setModalOpen} product={selected} />

      {/* Filtros: categoria. Sem faixa numérica — a `ListFiltersDialog` só sabe
          mascarar DINHEIRO (#157), e saldo é quantidade (que tem parser próprio,
          #71); uma faixa em reais recortando unidades seria o campo errado. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte a posição de estoque por categoria."
        groups={filterGroups}
        value={{ groups: { [CATEGORY_GROUP_KEY]: category }, range: { min: null, max: null } }}
        onApply={(next) => setCategory(next.groups[CATEGORY_GROUP_KEY] ?? ALL_CATEGORIES)}
      />
    </MainLayout>
  );
}
